/* Command registry for the ⌘K palette.
   `useCommands()` assembles the live list on every render from:
     - tab switches (via useApp)
     - saved views (passed in from Dashboard — keeps view state single-source)
     - region jumps (via useRegion.openRegion)
     - reset-layout + help actions
   Stays dependency-free — the palette does its own fuzzy substring match. */

import { useMemo } from "react"
import { useApp, useRegion } from "./store"
import type { SavedView, UseViewsReturn } from "./useViews"

export interface Command {
  id: string
  label: string
  hint?: string
  icon?: string
  shortcut?: string
  keywords?: string[]
  run: () => void
}

interface RegionEntry {
  code: string
  name: string
  keywords: string[]
  icon: string
}

/* Static region list — matches the codes that backend region endpoints accept.
   Synonyms help users find regions via common press-room labels ("middle east"
   → MENA, "gulf of aden" → RED_SEA, etc.). */
const REGIONS: RegionEntry[] = [
  {
    code: "GLOBAL",
    name: "Global",
    keywords: ["world", "earth", "worldwide", "all"],
    icon: "🌐",
  },
  {
    code: "MENA",
    name: "Middle East & North Africa",
    keywords: ["middle east", "mideast", "arab", "gulf", "north africa"],
    icon: "🕌",
  },
  {
    code: "ASIA",
    name: "Asia",
    keywords: ["east asia", "southeast asia", "apac", "china", "japan", "korea"],
    icon: "🌏",
  },
  {
    code: "PAC",
    name: "Pacific",
    keywords: ["pacific", "transpac", "asia pacific", "oceania"],
    icon: "🌊",
  },
  {
    code: "ATL",
    name: "Atlantic",
    keywords: ["atlantic", "transatlantic", "north atlantic"],
    icon: "🌊",
  },
  {
    code: "AM",
    name: "Americas",
    keywords: ["americas", "latam", "north america", "south america"],
    icon: "🌎",
  },
  {
    code: "RED_SEA",
    name: "Red Sea",
    keywords: ["red sea", "bab el mandeb", "gulf of aden", "yemen", "houthi"],
    icon: "🚢",
  },
  {
    code: "SUEZ",
    name: "Suez Canal",
    keywords: ["suez", "canal", "egypt", "sumed"],
    icon: "🛳️",
  },
  {
    code: "US",
    name: "United States",
    keywords: ["united states", "usa", "america", "us"],
    icon: "🇺🇸",
  },
  {
    code: "EU",
    name: "Europe",
    keywords: ["europe", "european union", "eu", "schengen"],
    icon: "🇪🇺",
  },
  {
    code: "HORMUZ",
    name: "Strait of Hormuz",
    keywords: ["hormuz", "iran", "persian gulf", "oman"],
    icon: "⚓",
  },
  {
    code: "MALACCA",
    name: "Strait of Malacca",
    keywords: ["malacca", "singapore", "indonesia", "malaysia"],
    icon: "⚓",
  },
  {
    code: "PANAMA",
    name: "Panama Canal",
    keywords: ["panama", "canal", "central america"],
    icon: "🛳️",
  },
]

/* Inputs for `useCommands`. The caller (Dashboard) passes its live views
   list and a "load" callback that drives its own layout state — this keeps
   saved-views state single-source instead of forking via a second useViews
   call in the palette subtree. */
export interface UseCommandsInput {
  views: UseViewsReturn["views"]
  onLoadView: (view: SavedView) => void
}

export function useCommands(input: UseCommandsInput): Command[] {
  const tab = useApp((s) => s.tab)
  const setTab = useApp((s) => s.setTab)
  const triggerResetLayout = useApp((s) => s.triggerResetLayout)
  const openRegion = useRegion((s) => s.openRegion)
  const { views, onLoadView } = input

  return useMemo(() => {
    const cmds: Command[] = []

    // Tab switches — surface the inactive tab first so the default selected
    // row maps to the most-likely intent.
    if (tab !== "cockpit") {
      cmds.push({
        id: "tab:cockpit",
        label: "Switch to Cockpit",
        hint: "tab",
        icon: "▦",
        keywords: ["dashboard", "grid", "panels"],
        run: () => setTab("cockpit"),
      })
    }
    if (tab !== "globe") {
      cmds.push({
        id: "tab:globe",
        label: "Switch to World View",
        hint: "tab",
        icon: "🌐",
        keywords: ["globe", "map", "3d", "world"],
        run: () => setTab("globe"),
      })
    }

    // Views — both builtins and user-saved.
    for (const view of views) {
      cmds.push({
        id: `view:${view.id}`,
        label: `Load view: ${view.name}`,
        hint: view.builtin ? "view · builtin" : "view",
        icon: "◫",
        keywords: ["view", "layout", view.name.toLowerCase()],
        run: () => onLoadView(view),
      })
    }

    // Region jumps — invoke openRegion with the backend code.
    for (const r of REGIONS) {
      cmds.push({
        id: `region:${r.code}`,
        label: `Jump to region: ${r.name}`,
        hint: "region",
        icon: r.icon,
        keywords: ["region", r.code.toLowerCase(), ...r.keywords],
        run: () => openRegion(r.code),
      })
    }

    // Layout + help actions.
    cmds.push({
      id: "layout:reset",
      label: "Reset layout",
      hint: "action",
      icon: "↻",
      keywords: ["layout", "reset", "default", "grid"],
      run: () => triggerResetLayout(),
    })
    cmds.push({
      id: "help:about",
      label: "Open help / about",
      hint: "action",
      icon: "?",
      keywords: ["help", "about", "shortcuts", "keyboard"],
      run: () => {
        const lines = [
          "SupplyLens cockpit",
          "",
          "Keyboard shortcuts:",
          "  ⌘K / Ctrl-K — open command palette",
          "  K          — open command palette (when not typing)",
          "  ESC        — close palette or region modal",
          "",
          "Views can be saved / loaded from the views menu in the header.",
          "Panels are draggable / resizable; use RESET LAYOUT to restore defaults.",
        ]
        // Minimal modal — replaceable with a real about panel later without
        // touching the palette contract.
        window.alert(lines.join("\n"))
      },
    })

    return cmds
  }, [tab, setTab, triggerResetLayout, openRegion, views, onLoadView])
}

/* Fuzzy-substring match: every char of `query` must appear in `target` in
   order (but not necessarily adjacent). Case-insensitive. Empty query matches
   everything. Returns a boolean — ranking stays position-stable so the
   palette's order matches the registry's natural order. */
export function fuzzyMatch(query: string, target: string): boolean {
  if (!query) return true
  const q = query.toLowerCase()
  const t = target.toLowerCase()
  let qi = 0
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++
  }
  return qi === q.length
}

export function filterCommands(cmds: Command[], query: string): Command[] {
  if (!query.trim()) return cmds
  const q = query.trim()
  return cmds.filter((c) => {
    const haystack = [c.label, c.hint ?? "", ...(c.keywords ?? [])].join(" ")
    return fuzzyMatch(q, haystack)
  })
}
